import React, { useState, useEffect, useContext, useLayoutEffect, useCallback } from "react";
import { createPortal } from "react-dom";
import {
    Typography,
    Button,
    IconButton,
    Box,
    Stack,
    Fade,
    Chip,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import AutoStoriesOutlinedIcon from "@mui/icons-material/AutoStoriesOutlined";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { UserSettingsContext } from "../../../setup/routes-manager/RequireAuth";

const ANCHOR_ID = "vtrender-glossary-btn";

const TERMS = [
    { label: "POC", desc: "Point of Control" },
    { label: "VAH", desc: "Value Area High" },
    { label: "VAL", desc: "Value Area Low" },
    { label: "IB", desc: "Initial Balance" },
];

function getAnchorRect(anchorId) {
    const el = document.getElementById(anchorId);
    if (!el) return null;
    return el.getBoundingClientRect();
}

function computePopoverStyle(anchorRect, cardWidth = 380) {
    const gap = 14;
    const viewportW = window.innerWidth;
    const margin = 16;

    let left = anchorRect.left + anchorRect.width / 2 - cardWidth / 2;
    left = Math.max(margin, Math.min(left, viewportW - cardWidth - margin));
    const top = anchorRect.bottom + gap;
    const arrowLeft = anchorRect.left + anchorRect.width / 2 - left;

    return { top, left, width: cardWidth, arrowLeft, placement: "bottom" };
}

function PopoverArrow({ arrowLeft }) {
    return (
        <Box
            sx={{
                position: "absolute",
                top: -9,
                left: arrowLeft,
                transform: "translateX(-50%)",
                width: 0,
                height: 0,
                borderLeft: "10px solid transparent",
                borderRight: "10px solid transparent",
                borderBottom: "10px solid #122a52",
                filter: "drop-shadow(0 -2px 2px rgba(0,0,0,0.15))",
            }}
        />
    );
}

export default function GlossaryWalkthroughCoach({
    open,
    onClose,
}) {
    const [layout, setLayout] = useState(null);

    const { setShowGlossaryPopup } = useContext(UserSettingsContext);

    const isOpen = Boolean(open);

    useEffect(() => {
        if (!isOpen) {
            setLayout(null);
        }
    }, [isOpen]);

    const updateLayout = useCallback(() => {
        if (!isOpen) {
            setLayout(null);
            return;
        }

        const anchorRect = getAnchorRect(ANCHOR_ID);
        if (!anchorRect) {
            setLayout({ fallback: true });
            return;
        }

        setLayout({
            fallback: false,
            anchorRect,
            popover: computePopoverStyle(anchorRect),
        });
    }, [isOpen]);

    useLayoutEffect(() => {
        if (!isOpen) return undefined;

        updateLayout();
        window.addEventListener("resize", updateLayout);
        window.addEventListener("scroll", updateLayout, true);

        return () => {
            window.removeEventListener("resize", updateLayout);
            window.removeEventListener("scroll", updateLayout, true);
        };
    }, [isOpen, updateLayout]);

    const dismiss = () => {
        onClose?.();
        setLayout(null);
    };

    const handleOpenGlossary = () => {
        if (setShowGlossaryPopup) {
            setShowGlossaryPopup(true);
        }
        dismiss();
    };

    if (!isOpen) return null;

    const cardContent = (
        <Box
            sx={{
                position: "relative",
                borderRadius: "16px",
                overflow: "visible",
                background:
                    "linear-gradient(165deg, #122a52 0%, #0b1e3d 55%, #091628 100%)",
                border: "1px solid rgba(99, 140, 255, 0.28)",
                boxShadow:
                    "0 20px 56px rgba(0, 0, 0, 0.55), 0 0 0 1px rgba(255,255,255,0.04) inset",
                color: "#ffffff",
            }}
        >
            <Box
                sx={{
                    height: 4,
                    borderRadius: "16px 16px 0 0",
                    background:
                        "linear-gradient(90deg, #3f69f8 0%, #5b8cff 45%, #4deeea 100%)",
                }}
            />

            {layout && !layout.fallback && (
                <PopoverArrow arrowLeft={layout.popover.arrowLeft} />
            )}

            <IconButton
                aria-label="close"
                onClick={() => dismiss()}
                size="small"
                sx={{
                    position: "absolute",
                    right: 10,
                    top: 14,
                    zIndex: 1,
                    color: "rgba(255, 255, 255, 0.55)",
                    backgroundColor: "rgba(255, 255, 255, 0.06)",
                    border: "1px solid rgba(255, 255, 255, 0.08)",
                    "&:hover": {
                        color: "#ffffff",
                        backgroundColor: "rgba(255, 255, 255, 0.12)",
                    },
                }}
            >
                <CloseIcon fontSize="small" />
            </IconButton>

            <Box sx={{ px: 2.75, pt: 2.5, pb: 2.75 }}>
                <Stack
                    direction="row"
                    spacing={1.75}
                    alignItems="flex-start"
                    sx={{ mb: 2, pr: 3.5 }}
                >
                    <Box
                        sx={{
                            width: 48,
                            height: 48,
                            borderRadius: "13px",
                            flexShrink: 0,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            background:
                                "linear-gradient(135deg, rgba(63,105,248,0.35) 0%, rgba(77,238,234,0.15) 100%)",
                            border: "1px solid rgba(99, 140, 255, 0.35)",
                        }}
                    >
                        <InfoOutlinedIcon sx={{ fontSize: 26, color: "#7aa8ff" }} />
                    </Box>

                    <Box sx={{ minWidth: 0 }}>
                        <Chip
                            icon={
                                <AutoStoriesOutlinedIcon
                                    sx={{
                                        fontSize: "14px !important",
                                        color: "#8eb4ff !important",
                                    }}
                                />
                            }
                            label="Learn · Glossary"
                            size="small"
                            sx={{
                                height: 24,
                                mb: 0.75,
                                color: "#b8ccff",
                                backgroundColor: "rgba(63, 105, 248, 0.14)",
                                border: "1px solid rgba(99, 140, 255, 0.28)",
                                fontWeight: 600,
                                fontSize: "0.68rem",
                                letterSpacing: "0.4px",
                                "& .MuiChip-icon": { ml: "6px" },
                            }}
                        />
                        <Typography
                            id="glossary-walkthrough-dialog-title"
                            variant="h6"
                            component="h2"
                            sx={{
                                fontWeight: 700,
                                fontSize: "1.05rem",
                                lineHeight: 1.35,
                            }}
                        >
                            POC? VAH? Initial Balance?
                        </Typography>
                    </Box>
                </Stack>

                <Typography
                    variant="body2"
                    sx={{
                        color: "rgba(224, 232, 255, 0.82)",
                        lineHeight: 1.65,
                        fontSize: "0.88rem",
                        mb: 2,
                    }}
                >
                    If you come across a term you&apos;re unsure about, the
                    Glossary is always available at the top right.
                </Typography>

                <Box
                    sx={{
                        display: "grid",
                        gridTemplateColumns: "1fr 1fr",
                        gap: 0.75,
                        mb: 2.5,
                    }}
                >
                    {TERMS.map((item) => (
                        <Box
                            key={item.label}
                            sx={{
                                px: 1.25,
                                py: 1,
                                borderRadius: "9px",
                                backgroundColor: "rgba(255, 255, 255, 0.04)",
                                border: "1px solid rgba(255, 255, 255, 0.07)",
                            }}
                        >
                            <Typography
                                sx={{
                                    fontSize: "0.75rem",
                                    fontWeight: 700,
                                    color: "#4deeea",
                                }}
                            >
                                {item.label}
                            </Typography>
                            <Typography
                                sx={{
                                    fontSize: "0.7rem",
                                    color: "rgba(200, 214, 245, 0.65)",
                                }}
                            >
                                {item.desc}
                            </Typography>
                        </Box>
                    ))}
                </Box>

                <Stack direction="row" spacing={1.25} alignItems="center">
                    <Button
                        variant="contained"
                        onClick={handleOpenGlossary}
                        endIcon={<ArrowForwardIcon sx={{ fontSize: 18 }} />}
                        sx={{
                            flex: 1,
                            py: 1.1,
                            borderRadius: "10px",
                            textTransform: "none",
                            fontWeight: 600,
                            fontSize: "0.88rem",
                            background:
                                "linear-gradient(135deg, #3f69f8 0%, #4a7aff 100%)",
                            boxShadow: "0 6px 20px rgba(63, 105, 248, 0.4)",
                            whiteSpace: "nowrap",
                            "&:hover": {
                                background:
                                    "linear-gradient(135deg, #355de0 0%, #3f69f8 100%)",
                            },
                        }}
                    >
                        Open Glossary
                    </Button>

                    <Button
                        variant="outlined"
                        onClick={() => dismiss()}
                        sx={{
                            flexShrink: 0,
                            py: 1.05,
                            px: 2,
                            borderRadius: "10px",
                            textTransform: "none",
                            fontWeight: 500,
                            fontSize: "0.88rem",
                            color: "rgba(200, 214, 245, 0.75)",
                            borderColor: "rgba(255, 255, 255, 0.25)",
                            "&:hover": {
                                color: "#ffffff",
                                borderColor: "rgba(255, 255, 255, 0.45)",
                                backgroundColor: "rgba(255, 255, 255, 0.06)",
                            },
                        }}
                    >
                        Not now
                    </Button>
                </Stack>
            </Box>
        </Box>
    );

    return createPortal(
        <Fade in={isOpen} timeout={280}>
            <Box
                sx={{
                    position: "fixed",
                    inset: 0,
                    zIndex: 1600,
                    pointerEvents: "none",
                }}
            >
                <Box
                    onClick={() => dismiss()}
                    sx={{
                        position: "absolute",
                        inset: 0,
                        backgroundColor: "rgba(4, 12, 28, 0.62)",
                        backdropFilter: "blur(2px)",
                        pointerEvents: "auto",
                    }}
                />

                {layout && !layout.fallback && layout.anchorRect && (
                    <Box
                        sx={{
                            position: "fixed",
                            top: layout.anchorRect.top - 5,
                            left: layout.anchorRect.left - 5,
                            width: layout.anchorRect.width + 10,
                            height: layout.anchorRect.height + 10,
                            borderRadius: "10px",
                            border: "2px solid #4deeea",
                            boxShadow:
                                "0 0 0 4px rgba(77, 238, 234, 0.2), 0 0 28px rgba(63, 105, 248, 0.55)",
                            animation: "glossaryCoachPulse 2s ease-in-out infinite",
                            pointerEvents: "none",
                            zIndex: 1,
                            "@keyframes glossaryCoachPulse": {
                                "0%, 100%": {
                                    boxShadow:
                                        "0 0 0 4px rgba(77, 238, 234, 0.2), 0 0 28px rgba(63, 105, 248, 0.55)",
                                },
                                "50%": {
                                    boxShadow:
                                        "0 0 0 8px rgba(77, 238, 234, 0.12), 0 0 36px rgba(63, 105, 248, 0.7)",
                                },
                            },
                        }}
                    />
                )}

                <Box
                    sx={{
                        position: "fixed",
                        pointerEvents: "auto",
                        zIndex: 2,
                        ...(layout?.fallback || !layout?.popover
                            ? {
                                  top: "50%",
                                  left: "50%",
                                  transform: "translate(-50%, -50%)",
                                  width: "min(380px, calc(100vw - 32px))",
                              }
                            : {
                                  top: layout.popover.top,
                                  left: layout.popover.left,
                                  width: layout.popover.width,
                              }),
                    }}
                >
                    {cardContent}
                </Box>
            </Box>
        </Fade>,
        document.body,
    );
}
